import { AuthResponse, LoginCredentials, RegisterData } from '../../models/User';
import { AuthApi } from '../api/AuthApi';
import { BaseApplicationService } from './BaseApplicationService';
import { Logger } from '../../utils/logger';
import { getErrorMessage, toError } from '../../utils/errorMessage';
import { ApplicationError } from '../../errors';

/**
 * Service for handling all authentication-related operations
 * Orchestrates authentication flow with error handling and business logic
 */
export class AuthService extends BaseApplicationService {
  private static readonly SERVICE_NAME = 'AuthService';

  /**
   * Standardized error handling for authentication operations
   * @param methodName Name of the method for logging
   * @param operation The actual async operation to perform
   * @param metadata Additional metadata for logging
   * @returns Standardized auth response format
   */
  private static async executeAuthOperation(
    methodName: string,
    operation: () => Promise<AuthResponse>,
    metadata?: Record<string, any>
  ): Promise<AuthResponse> {
    try {
      const result = await operation();

      if (result.error) {
        Logger.serviceError(this.SERVICE_NAME, methodName, new Error(result.error), {
          operation: `${methodName}_api_error`,
          ...this.formatMetadata(metadata),
        });
      }
      // Removed success logging to reduce console noise

      return result;
    } catch (err: unknown) {
      Logger.serviceError(this.SERVICE_NAME, methodName, toError(err), {
        operation: `${methodName}_catch`,
        ...this.formatMetadata(metadata),
      });
      return {
        user: null,
        session: null,
        error: this.extractErrorMessage(err),
      };
    }
  }

  /**
   * Sign in with email and password
   * @param credentials Login credentials
   * @returns Authentication response with user, session, and error
   */
  static async signIn(credentials: LoginCredentials): Promise<AuthResponse> {
    const validationError = this.validateInput(this.SERVICE_NAME, [
      { condition: !!credentials.email, message: 'Email is required' },
      { condition: !!credentials.password, message: 'Password is required' },
      { condition: /\S+@\S+\.\S+/.test(credentials.email), message: 'Valid email is required' },
    ]);

    if (validationError) {
      return { user: null, session: null, error: validationError };
    }

    return this.executeAuthOperation('signIn', async () => await AuthApi.signIn(credentials), {
      email: credentials.email,
    });
  }

  /**
   * Sign up new user
   * @param registerData Registration data
   * @returns Authentication response with user, session, and error
   */
  static async signUp(registerData: RegisterData): Promise<AuthResponse> {
    const validationError = this.validateInput(this.SERVICE_NAME, [
      { condition: !!registerData.email, message: 'Email is required' },
      { condition: !!registerData.password, message: 'Password is required' },
      { condition: !!registerData.name, message: 'Name is required' },
      { condition: /\S+@\S+\.\S+/.test(registerData.email), message: 'Valid email is required' },
      {
        condition: registerData.password.length >= 6,
        message: 'Password must be at least 6 characters',
      },
    ]);

    if (validationError) {
      return { user: null, session: null, error: validationError };
    }

    return this.executeAuthOperation('signUp', async () => await AuthApi.signUp(registerData), {
      email: registerData.email,
      role: registerData.role,
    });
  }

  /**
   * Manager-only user creation through the create-user Edge Function.
   * Unlike signUp this does not swap the manager's session and allows
   * creating managers/sellers (server-side role check enforces manager).
   */
  static async createUser(payload: {
    name: string;
    email: string;
    password: string;
    role: 'manager' | 'seller' | 'worker';
  }): Promise<{
    user: { id: string; email: string; name: string; role: string } | null;
    error: string | null;
  }> {
    const validationError = this.validateInput(this.SERVICE_NAME, [
      { condition: !!payload.email, message: 'Email is required' },
      { condition: !!payload.password, message: 'Password is required' },
      { condition: !!payload.name, message: 'Name is required' },
      { condition: /\S+@\S+\.\S+/.test(payload.email), message: 'Valid email is required' },
      {
        condition: payload.password.length >= 6,
        message: 'Password must be at least 6 characters',
      },
    ]);

    if (validationError) {
      return { user: null, error: validationError };
    }

    try {
      return await AuthApi.createUser(payload);
    } catch (err: unknown) {
      Logger.serviceError(this.SERVICE_NAME, 'createUser', toError(err), {
        email: payload.email,
        role: payload.role,
        operation: 'create_user_catch',
      });
      return { user: null, error: getErrorMessage(err, 'Failed to create user') };
    }
  }

  /**
   * Sign out current user
   * @returns Error status
   */
  static async signOut(): Promise<{ error: string | null }> {
    return this.executeSuccessOperation(this.SERVICE_NAME, 'signOut', async () => {
      const result = await AuthApi.signOut();
      if (result.error) {
        throw new ApplicationError(result.error);
      }
    }).then(result => ({ error: result.error }));
  }

  /**
   * Send a password reset email
   * @returns Error message, or null on success
   */
  static async sendPasswordResetEmail(email: string): Promise<{ error: string | null }> {
    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      return { error: 'Valid email is required' };
    }
    return AuthApi.sendPasswordResetEmail(email);
  }

  /**
   * Set a new password (used on the reset-password page)
   * @returns Error message, or null on success
   */
  static async updatePassword(password: string): Promise<{ error: string | null }> {
    if (!password || password.length < 6) {
      return { error: 'Password must be at least 6 characters' };
    }
    return AuthApi.updatePassword(password);
  }

  /**
   * Get current session and user
   * @returns Authentication response with current user and session
   */
  static async getCurrentSession(): Promise<AuthResponse> {
    return this.executeAuthOperation(
      'getCurrentSession',
      async () => await AuthApi.getCurrentSession()
    );
  }
}
